/**
 * AdModal Component
 * Reusable modal for displaying post-download advertisements
 * Uses React Portal to render at document.body level for proper z-index stacking
 */

import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface AdModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  adContent?: ReactNode;
}

export function AdModal({
  isOpen,
  onClose,
  title,
  message,
  primaryButtonText = 'Close',
  secondaryButtonText,
  onPrimaryButtonClick,
  adContent,
}: AdModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      console.log('[AdModal] Modal z-index debugging:');
      console.log('[AdModal] Modal element:', modalRef.current);
      console.log('[AdModal] Modal computed z-index:', window.getComputedStyle(modalRef.current).zIndex);
      console.log('[AdModal] Modal position:', window.getComputedStyle(modalRef.current).position);

      // Check all stacking contexts
      let parent = modalRef.current.parentElement;
      let depth = 0;
      while (parent && depth < 20) {
        const styles = window.getComputedStyle(parent);
        const zIndex = styles.zIndex;
        const position = styles.position;
        const transform = styles.transform;
        const willChange = styles.willChange;

        if (position !== 'static' || transform !== 'none' || willChange !== 'auto') {
          console.log(`[AdModal] Parent ${depth}:`, {
            tagName: parent.tagName,
            className: parent.className,
            zIndex,
            position,
            transform,
            willChange,
          });
        }
        parent = parent.parentElement;
        depth++;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !adContent && containerRef.current) {
      // Clear container
      containerRef.current.innerHTML = '';

      // Create an iframe to isolate the ad
      const iframe = document.createElement('iframe');
      iframe.title = 'HilltopAds Advertisement';
      iframe.style.width = '100%';
      iframe.style.height = '250px';
      iframe.style.border = 'none';
      iframe.style.overflow = 'hidden';

      containerRef.current.appendChild(iframe);

      // Write content to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { overflow: hidden; }
            </style>
          </head>
          <body>
            <script>
              (function(vch){
                var d = document,
                    s = d.createElement('script'),
                    l = d.scripts[d.scripts.length - 1];
                s.settings = vch || {};
                s.src = "//remarkablebottom.com/bzXrV.sBdgGUl-0tYZWGcm/Oe/mW9ZuVZ/UYl/krP/TAYr3NNzjzEf2vMRDykSt_NUjQcR2/MLTZYZxqMzAr";
                s.async = true;
                s.referrerPolicy = 'no-referrer-when-downgrade';
                l.parentNode.insertBefore(s, l);
              })({})
            </script>
          </body>
          </html>
        `);
        iframeDoc.close();
      }

      // Cleanup
      return () => {
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // Remove any floating ads from main page
        document.querySelectorAll('iframe[src*="remarkablebottom"], iframe[src*="hilltop"]').forEach(el => {
          const style = window.getComputedStyle(el);
          if (style.position === 'fixed' || style.position === 'absolute') {
            el.remove();
          }
        });
      };
    }
  }, [isOpen, adContent]);

  if (!isOpen) return null;

  const handlePrimaryClick = () => {
    if (onPrimaryButtonClick) {
      onPrimaryButtonClick();
    }
    onClose();
  };

  // Use portal to render at document.body level
  return createPortal(
    <div ref={modalRef} className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 999999 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full relative shadow-2xl">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {message}
        </p>

        {/* Ad content placeholder */}
        {adContent || <div ref={containerRef} className="mb-4" />}

        <div className="flex gap-3">
          <button onClick={handlePrimaryClick} className="btn-primary flex-1">
            {primaryButtonText}
          </button>
          {secondaryButtonText && (
            <button
              onClick={onClose}
              className="btn-secondary dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 flex-1"
            >
              {secondaryButtonText}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
